import {useState} from "react";
import {Button, Card, Col, DatePicker, Form, message, Row, Statistic, Table} from "antd";
import {PrinterOutlined, SearchOutlined} from "@ant-design/icons";
import Api from "../../services/Api";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import flexCurrency from "../../utils/FlexCurrency";

dayjs.locale("tr");
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const {RangePicker} = DatePicker;

const printStyle = `
  .rst-aralik-table .ant-table-cell { padding: 3px !important; }
  @media print {
    body * { visibility: hidden; }
    #rst-aralik-print-area, #rst-aralik-print-area * { visibility: visible; }
    #rst-aralik-print-area { position: absolute; top: 0; left: 0; width: 100%; }
    .no-print { display: none !important; }
  }
`;

const RENKLER = {
    teras: "#1677ff",
    bufe:  "#fa8c16",
    salon: "#722ed1",
    genel: "#52c41a",
};

const columns = [
    {
        title: "Tarih",
        dataIndex: "Gun",
        render: g => dayjs(g).format("DD MMMM YYYY dddd"),
    },
    {
        title: <span style={{color: RENKLER.teras}}>Teras (₺)</span>,
        dataIndex: "TerasToplam",
        className: "text-align-right",
        render: v => <span style={{color: RENKLER.teras}}>{flexCurrency(v)}</span>,
    },
    {
        title: <span style={{color: RENKLER.bufe}}>Büfe (₺)</span>,
        dataIndex: "BufeToplam",
        className: "text-align-right",
        render: v => <span style={{color: RENKLER.bufe}}>{flexCurrency(v)}</span>,
    },
    {
        title: <span style={{color: RENKLER.salon}}>Salon (₺)</span>,
        dataIndex: "SalonToplam",
        className: "text-align-right",
        render: v => <span style={{color: RENKLER.salon}}>{flexCurrency(v)}</span>,
    },
    {
        title: "Genel Toplam (₺)",
        dataIndex: "GenelToplam",
        className: "text-align-right",
        render: v => <b style={{color: RENKLER.genel}}>{flexCurrency(v)}</b>,
    },
];

const groupByMonth = (items) => {
    const groups = {};
    items.forEach(item => {
        const ay = dayjs(item.Gun).format("YYYY-MM");
        if (!groups[ay]) groups[ay] = [];
        groups[ay].push(item);
    });

    return Object.keys(groups)
        .sort((a, b) => b.localeCompare(a))
        .map(ay => {
            const gunler = [...groups[ay]].sort(
                (a, b) => new Date(b.Gun) - new Date(a.Gun)
            );
            const total = {
                TerasToplam: gunler.reduce((s, i) => s + i.TerasToplam, 0),
                BufeToplam:  gunler.reduce((s, i) => s + i.BufeToplam,  0),
                SalonToplam: gunler.reduce((s, i) => s + i.SalonToplam, 0),
                GenelToplam: gunler.reduce((s, i) => s + i.GenelToplam, 0),
            };
            const label = dayjs(`${ay}-01`).format("MMMM YYYY");
            return {ay, label, gunler, total};
        });
};

/* ── Ay özet şeridi ─────────────────────────────────────── */
const OzetSerit = ({total}) => (
    <Row
        gutter={[16, 8]}
        style={{padding: "10px 16px", background: "#fafafa", borderBottom: "1px solid #f0f0f0"}}
    >
        <Col xs={12} sm={6}>
            <Statistic
                title="Teras"
                value={total.TerasToplam}
                suffix="₺"
                valueStyle={{fontSize: 13, color: RENKLER.teras}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
        <Col xs={12} sm={6}>
            <Statistic
                title="Büfe"
                value={total.BufeToplam}
                suffix="₺"
                valueStyle={{fontSize: 13, color: RENKLER.bufe}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
        <Col xs={12} sm={6}>
            <Statistic
                title="Salon"
                value={total.SalonToplam}
                suffix="₺"
                valueStyle={{fontSize: 13, color: RENKLER.salon}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
        <Col xs={12} sm={6}>
            <Statistic
                title="Toplam Tutar"
                value={total.GenelToplam}
                suffix="₺"
                valueStyle={{fontSize: 14, color: RENKLER.genel, fontWeight: 700}}
                formatter={v => flexCurrency(v)}
            />
        </Col>
    </Row>
);

/* ── Genel özet kartı ───────────────────────────────────── */
const GenelOzet = ({ozet}) => (
    <Card
        style={{
            marginBottom: 12,
            background: "linear-gradient(135deg, #f6ffed 0%, #e6f4ff 100%)",
            border: "1px solid #91caff",
        }}
        bodyStyle={{padding: "12px 16px"}}
    >
        <Row align="middle" gutter={[24, 8]}>
            <Col xs={24} sm={4}>
                <div style={{fontSize: 14, fontWeight: 800, color: "#1677ff"}}>
                    {ozet.startDate}
                </div>
                <div style={{fontSize: 12, color: "#888"}}>—</div>
                <div style={{fontSize: 14, fontWeight: 800, color: "#1677ff"}}>
                    {ozet.endDate}
                </div>
            </Col>
            <Col xs={12} sm={5}>
                <Statistic
                    title="Teras"
                    value={ozet.TerasToplam}
                    suffix="₺"
                    valueStyle={{fontSize: 14, color: RENKLER.teras, fontWeight: 600}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
            <Col xs={12} sm={5}>
                <Statistic
                    title="Büfe"
                    value={ozet.BufeToplam}
                    suffix="₺"
                    valueStyle={{fontSize: 14, color: RENKLER.bufe, fontWeight: 600}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
            <Col xs={12} sm={5}>
                <Statistic
                    title="Salon"
                    value={ozet.SalonToplam}
                    suffix="₺"
                    valueStyle={{fontSize: 14, color: RENKLER.salon, fontWeight: 600}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
            <Col xs={12} sm={5}>
                <Statistic
                    title="Genel Toplam"
                    value={ozet.GenelToplam}
                    suffix="₺"
                    valueStyle={{fontSize: 16, color: RENKLER.genel, fontWeight: 800}}
                    formatter={v => flexCurrency(v)}
                />
            </Col>
        </Row>
    </Card>
);

/* ── Ana bileşen ────────────────────────────────────────── */
const RestaurantTarihAralikliOzetRapor = () => {
    const [monthData, setMonthData] = useState([]);
    const [ozet, setOzet]           = useState(null);
    const [loading, setLoading]     = useState(false);
    const [form] = Form.useForm();

    const getData = (values) => {
        const startDate = dayjs(values.tarihAraligi[0]).startOf("day");
        const endDate   = dayjs(values.tarihAraligi[1]).endOf("day");

        setLoading(true);
        setOzet(null);
        setMonthData([]);

        Api.get("api/ToyaPos/GunlukOzet")
            .then(res => {
                const filtrelenmis = res.data.filter(item =>
                    dayjs(item.Gun).isSameOrAfter(startDate) &&
                    dayjs(item.Gun).isSameOrBefore(endDate)
                );

                if (filtrelenmis.length === 0) {
                    message.warning("Seçilen tarih aralığında veri bulunamadı.");
                    return;
                }

                setMonthData(groupByMonth(filtrelenmis));
                setOzet({
                    startDate:   startDate.format("DD.MM.YYYY"),
                    endDate:     endDate.format("DD.MM.YYYY"),
                    TerasToplam: filtrelenmis.reduce((s, i) => s + i.TerasToplam, 0),
                    BufeToplam:  filtrelenmis.reduce((s, i) => s + i.BufeToplam,  0),
                    SalonToplam: filtrelenmis.reduce((s, i) => s + i.SalonToplam, 0),
                    GenelToplam: filtrelenmis.reduce((s, i) => s + i.GenelToplam, 0),
                });
                message.success("Rapor verileri başarıyla alındı.");
            })
            .catch(() => message.error("Veriler alınırken hata oluştu."))
            .finally(() => setLoading(false));
    };

    return (
        <div className={"p-1"}>
            <style>{printStyle}</style>

            {/* Filtre bar */}
            <Card
                className={"no-print"}
                style={{marginBottom: 12}}
                bodyStyle={{padding: "10px 16px"}}
            >
                <Form
                    form={form}
                    onFinish={getData}
                    layout={"inline"}
                    initialValues={{tarihAraligi: [dayjs().startOf("month"), dayjs()]}}
                >
                    <Form.Item
                        label={"Tarih Aralığı"}
                        name={"tarihAraligi"}
                        rules={[{required: true, message: "Lütfen tarih aralığı seçiniz."}]}
                    >
                        <RangePicker
                            format={"DD.MM.YYYY"}
                            allowClear={false}
                            presets={[
                                {label: "Bu Hafta",  value: [dayjs().startOf("week"), dayjs()]},
                                {label: "Bu Ay",     value: [dayjs().startOf("month"), dayjs()]},
                                {label: "Geçen Ay",  value: [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")]},
                                {label: "Son 3 Ay",  value: [dayjs().subtract(3, "month"), dayjs()]},
                                {label: "Bu Yıl",    value: [dayjs().startOf("year"), dayjs()]},
                            ]}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button type={"primary"} htmlType={"submit"} icon={<SearchOutlined/>} loading={loading}>
                            Raporu Getir
                        </Button>
                    </Form.Item>
                    {ozet && (
                        <Form.Item>
                            <Button icon={<PrinterOutlined/>} onClick={() => window.print()}>
                                Yazdır
                            </Button>
                        </Form.Item>
                    )}
                </Form>
            </Card>

            <div id={"rst-aralik-print-area"}>
                {ozet && <GenelOzet ozet={ozet}/>}

                {monthData.map(({ay, label, gunler, total}) => (
                    <Card
                        key={ay}
                        size={"small"}
                        title={<span style={{fontWeight: 600}}>{label}</span>}
                        bodyStyle={{padding: 0}}
                        style={{marginBottom: 8}}
                    >
                        <OzetSerit total={total}/>
                        <Table
                            className={"rst-aralik-table"}
                            size={"small"}
                            bordered
                            dataSource={gunler}
                            rowKey={r => r.Gun}
                            columns={columns}
                            pagination={false}
                        />
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default RestaurantTarihAralikliOzetRapor;
